import { FC, useEffect, useState } from 'react';
import { Feature } from '@shinetools/pricing-plan-library';

import { ProviderPlanId } from '__generated__/GQL';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import { getInsuranceSummaryUrl } from 'common/insurance';
import Button from 'components/_core/Button';
import Checkbox from 'components/_core/form/Checkbox';
import IconButton from 'components/_core/IconButton';
import SunshineCard from 'components/_core/SunshineCard';
import BackButton from 'components/BackButton';
import PdfPreview from 'components/DocumentPreview/PdfPreview';
import Loader from 'components/Loader';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import * as Layout from 'layouts/StepperLayout';

import useSwitchToPlan from './hooks/useSwitchToPlan';
import locales from './locales';
import * as S from './styles';

const HIDING_TOOLBAR_QUERY_PARAMS = '#toolbar=0';

interface UpgradeInsuranceTermsAndConditionProps extends StepProps {
  providerPlanId: ProviderPlanId;
}

const UpgradeInsuranceTermsAndCondition: FC<
  UpgradeInsuranceTermsAndConditionProps
> = ({ goBack, goNext, providerPlanId }) => {
  const { isFeatureAvailable, plan } = useCurrentPricingPlan();
  const [upgrade, { loading }] = useSwitchToPlan(providerPlanId);

  const [agreeToTerms, setAgreeToTerms] = useState(false);

  const submit = async () => {
    await upgrade();
    goNext();
  };

  const hasAlreadyAccessToInsuranceAssistance = isFeatureAvailable(
    Feature.InsuranceAssistance,
  );

  useEffect(() => {
    if (hasAlreadyAccessToInsuranceAssistance) {
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAlreadyAccessToInsuranceAssistance]);

  if (hasAlreadyAccessToInsuranceAssistance || !plan) {
    return <Loader />;
  }

  const insuranceSummaryUrl = getInsuranceSummaryUrl(providerPlanId);

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton onClick={goBack} />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <S.Title>{locales.title}</S.Title>
          <SunshineCard
            flex={1}
            marginBottom="space-40"
            sx={{
              '& > div': {
                display: 'flex',
                flexDirection: 'column',
                height: '100%', // Make the embed pdf take all the empty space
              },
            }}
          >
            <PdfPreview
              fileSrc={`${insuranceSummaryUrl}${HIDING_TOOLBAR_QUERY_PARAMS}`}
            />

            <S.TermsAgreementContainer>
              <S.TermsAgreementLabel htmlFor="agree-to-terms">
                <Checkbox
                  id="agree-to-terms"
                  isChecked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                >
                  <S.TermsAgreementLabelText data-testid="input-agree-to-terms">
                    {locales.confirmText}
                  </S.TermsAgreementLabelText>
                </Checkbox>
              </S.TermsAgreementLabel>

              <IconButton
                aria-label=""
                as="a"
                href={insuranceSummaryUrl}
                icon="download"
                rel="noopener noreferrer"
                target="_blank"
                variant="secondary"
              />
            </S.TermsAgreementContainer>
          </SunshineCard>
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <StepperProgressBar />
          <Layout.FooterButtonsContainer>
            <Button
              data-testid="btn-insurance-terms-next"
              icon="arrow-right"
              isDisabled={!agreeToTerms}
              isLoading={loading}
              onClick={submit}
            >
              {locales.next}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default UpgradeInsuranceTermsAndCondition;
